import React, { useEffect, useState } from 'react';

import clsx from 'clsx';
import IconArrowDown from '@/assets/icons/icon-arrow-down-01.svg?react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import IconThumbsUpRectangle from '@/assets/icons/icon-thumbs-up-rectangle.svg?react';
import { ImageSocialPreview } from '@/components/inspector-fields/ImageSocialPreview/ImageSocialPreview';
import { Textarea } from '@/components/ui/textarea';
import { TextField } from '@/components/inspector-fields/TextField/TextField';
import { InfoTooltip } from '@/components/tooltips/InfoTooltip/InfoTooltip';
import type { SocialSettings } from '@builder4/shared/types/settings';
import type { AssetResponse } from '@lp/lib-asset-api';
import { selectSocialSettings } from '@/store/settings/social/selectors';
import { selectCurrentPageId } from '@/store/pages/selectors';
import { useAppSelector } from '@/store/hooks';
import { shallowEqual } from '@/utils/settingsHelpers';
import { useSettingsDebounceUpdate } from '@/hooks/useSettingsDebounceUpdate';

interface SettingsSocialProps {
  className: string;
  onClick?: () => void;
  isOpenCollapse: boolean;
}

const defaultSettings: SocialSettings = {
  socialPreview: null,
  socialPreviewImageId: null,
  socialUrl: '',
  socialPageTitle: '',
  socialDescription: '',
};

export const SettingsSocial: React.FC<SettingsSocialProps> = ({
  className = '',
  onClick,
  isOpenCollapse,
}) => {
  const socialSettingsRedux = useAppSelector(selectSocialSettings) ?? defaultSettings;
  const currentPageId = useAppSelector(selectCurrentPageId);
  const [isOpen] = useState(isOpenCollapse);
  const [socialSettings, setSocialSettings] = useState<SocialSettings>(socialSettingsRedux);

  const handleClick = () => {
    if (onClick) onClick();
  };

  const handleUpdateSocialSettings = <K extends keyof SocialSettings>(
    key: K,
    value: SocialSettings[K],
  ) => {
    setSocialSettings((prevState) => ({ ...prevState, [key]: value }));
  };

  const handleImageSelect = (image: AssetResponse) => {
    setSocialSettings((prevState) => ({
      ...prevState,
      socialPreview: image.fileUri,
      socialPreviewImageId: image._meta.id,
    }));
  };

  const handleImageRemove = () => {
    setSocialSettings((prevState) => ({
      ...prevState,
      socialPreview: null,
      socialPreviewImageId: null,
    }));
  };

  const { lastSavedValueRef } = useSettingsDebounceUpdate({
    value: socialSettings,
    bodyKey: 'social_settings',
    currentPageId: currentPageId,
    reduxValue: socialSettingsRedux,
  });

  useEffect(() => {
    if (!socialSettingsRedux) return;

    setSocialSettings((prev) => {
      if (shallowEqual(prev, socialSettingsRedux)) return prev;
      // Guard against our own save response overwriting local state when stale
      if (
        lastSavedValueRef.current &&
        shallowEqual(socialSettingsRedux, lastSavedValueRef.current)
      ) {
        return prev;
      }
      return socialSettingsRedux;
    });
  }, [socialSettingsRedux, lastSavedValueRef]);

  return (
    <Collapsible defaultOpen={isOpen}>
      <CollapsibleTrigger
        onClick={handleClick}
        data-qa-selector="settings-social-toggle"
        className={clsx(
          'group bg-neutral-0 w-full cursor-pointer border-t border-neutral-100 px-2 py-4',
          className,
        )}
      >
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <IconThumbsUpRectangle className="h-4 w-4 text-neutral-900" />
            <span className="text-xs text-neutral-900">Social</span>
          </div>
          <IconArrowDown className="h-4 w-4 text-neutral-500 transition-all group-aria-expanded:rotate-180" />
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent className={clsx('bg-neutral-0 max-w-[232px] pb-3')}>
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-2 p-2">
            <p className="text-button-sm font-medium text-neutral-500">Social Preview</p>
            <ImageSocialPreview
              imageSrc={socialSettings.socialPreview ?? null}
              imageId={socialSettings.socialPreviewImageId ?? null}
              socialUrl={socialSettings.socialUrl}
              socialPageTitle={socialSettings.socialPageTitle}
              socialDescription={socialSettings.socialDescription}
              onImageSelect={handleImageSelect}
              onImageRemove={handleImageRemove}
              variant="default"
            />
          </div>

          <div className="flex flex-col gap-2 p-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <p className="text-button-sm text-neutral-500">Social URL</p>
                <InfoTooltip text="The web address social platforms will display when someone shares your page. Leave blank to use your actual page URL, or enter a custom URL if you want something different to appear." />
              </div>
            </div>
            <TextField
              className="w-full max-w-[216px]"
              classNameInputParent="w-full max-w-full"
              inputValue={socialSettings.socialUrl ?? ''}
              onChange={(e) => handleUpdateSocialSettings('socialUrl', e.target.value)}
              placeholderText="Social URL"
              state="default"
              text=""
              type="text"
            />
            <p className="text-[10px] font-medium text-neutral-400">
              This is the public URL Facebook will use for this Page. Leave it blank to use the link
              you share.
            </p>
          </div>

          <div className="flex flex-col gap-2 p-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <p className="text-button-sm text-neutral-500">Social Page Title</p>
                <InfoTooltip text="The headline that appears when someone shares your page on social media and messaging services." />
              </div>
            </div>
            <TextField
              className="w-full max-w-[216px]"
              classNameInputParent="w-full max-w-full"
              inputValue={socialSettings.socialPageTitle ?? ''}
              onChange={(e) => handleUpdateSocialSettings('socialPageTitle', e.target.value)}
              placeholderText="Social Page Title"
              state="default"
              text=""
              type="text"
            />
            <p className="text-[10px] font-medium text-neutral-400">Keep this clear and concise.</p>
          </div>

          <div className="flex flex-col gap-2 p-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <p className="text-button-sm text-neutral-500">Social Page Description</p>
                <InfoTooltip text="The preview text that appears when someone shares your page on social media and messaging services. Explain what visitors will get on this page." />
              </div>
            </div>
            <Textarea
              className={'min-h-16 w-full resize-none'}
              placeholder={'Page description'}
              disabled={false}
              value={socialSettings.socialDescription ?? ''}
              onChange={(e) => handleUpdateSocialSettings('socialDescription', e.target.value)}
            />
            <p className="text-[10px] font-medium text-neutral-400">
              Keep this between 50 and 160 characters.
            </p>
          </div>

          <div className="my-3 flex flex-col items-start p-2 text-[10px] font-medium text-neutral-400">
            <p>
              Want to learn more about these fields? <br />
              <a
                href="https://support.leadpages.com/hc/en-us/articles/218459337"
                className="text-indigo-500 underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Click here
              </a>
            </p>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
